import axios from 'axios';
import { isReadOnly } from '@/lib/permissions';
import { toast } from '@/hooks/use-toast';

/**
 * Centralized API client used by all data hooks.
 * - Reads base URL from env (REACT_APP_API_URL)
 * - Attaches auth token from localStorage if present
 * - Blocks write requests for read-only roles (single enforcement chokepoint)
 * - 401 → clears auth and reloads (sends user back to login)
 */
export const API_URL =
  process.env.REACT_APP_API_URL || 'http://localhost:3000';

export const api = axios.create({
  baseURL: API_URL,
  timeout: 15_000,
});

// Methods that never change data - always allowed.
const SAFE_METHODS = new Set(['get', 'head', 'options']);

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }

  // Read-only roles (analyst/agency viewer) may never mutate. We block at the
  // client so EVERY write is stopped - a button we forgot to hide, a kanban
  // drag, an inline edit - all funnel through here. UI-level only; the API
  // hardening task adds the server-side guarantee.
  const method = (config.method || 'get').toLowerCase();
  if (!SAFE_METHODS.has(method) && isReadOnly()) {
    toast({
      title: 'Read-only access',
      description: 'Your role can view this data but not change it.',
      variant: 'destructive',
    });
    return Promise.reject(
      new axios.Cancel('Blocked: read-only role cannot perform write actions.'),
    );
  }

  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    // A 401 means "your session is over" only when a session was sent. The
    // sign-in doors answer a wrong password or a wrong code with 401 too, and
    // treating those as a dead session reloaded the page, so the error never
    // showed and the two-step challenge was lost. Under /api/auth/ only an
    // explicit session_ended counts (a wrong password at "move to a new
    // phone" must not sign the person out).
    const sentSession = Boolean(error.config?.headers?.Authorization);
    const url = String(error.config?.url || '');
    const authDoor = /\/api\/auth\//.test(url);
    const sessionOver = sentSession
      && (!authDoor || error.response?.data?.code === 'session_ended');
    if (error.response?.status === 401 && sessionOver) {
      // Clear stale auth; let app re-render to login
      try {
        localStorage.removeItem('token');
        localStorage.removeItem('userId');
        localStorage.removeItem('userName');
      } catch {}
      // Reload to re-render into the login/console state, but never in a tight
      // loop: cap to once per 30s so a pathological repeat-401 can't reload
      // the page endlessly.
      if (typeof window !== 'undefined') {
        try {
          const KEY = 'mm-401-last-reload';
          const last = Number(sessionStorage.getItem(KEY) || 0);
          const now = Date.now();
          if (!last || now - last > 30000) {
            sessionStorage.setItem(KEY, String(now));
            window.location.reload();
          }
        } catch { window.location.reload(); }
      }
    }
    return Promise.reject(error);
  }
);
